import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { FaArrowLeft } from 'react-icons/fa';
import { formatAUD } from '../utils/formatCurrency';
import { graphqlRequest } from '../utils/graphqlRequest';
import { BOOKING_SERVICES_CHANGED } from '../utils/bookingEvents';
import '../styles/ProfileDashboard.css';
import MainPage from './ProfilePages/components/MainPage';
import WriteEmailModal from './ProfilePages/components/WriteEmailModal';

const FIND_REQUIRED_SERVICES = `
  query FindBookingService($bookingId: String!) {
    findBookingService(bookingId: $bookingId) {
      data {
        id
        service {
          service
        }
      }
    }
  }
`;

export { notifyBookingServicesChanged } from '../utils/bookingEvents';

export default function ProfileDashboard({ company, pipeline, onBack }) {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [requiredServices, setRequiredServices] = useState([]);

  const token = Cookies.get('userName');

  useEffect(() => {
    if (company?.id) Cookies.set('bookingId', company.id, { path: '/' });
    return () => Cookies.remove('bookingId', { path: '/' });
  }, [company?.id]);

  const fetchRequiredServices = useCallback(async () => {
    if (!company?.id) {
      setRequiredServices([]);
      return;
    }
    try {
      const data = await graphqlRequest(FIND_REQUIRED_SERVICES, {
        bookingId: company.id,
      });
      const seen = new Set();
      const names = [];
      (data?.findBookingService?.data || []).forEach((row) => {
        const name = String(row?.service?.service || '').trim();
        const id = String(row?.service?.id || name);
        if (!name || seen.has(id)) return;
        seen.add(id);
        names.push({ id, name });
      });
      setRequiredServices(names);
    } catch (err) {
      console.error('Error fetching required services:', err);
    }
  }, [company?.id]);

  useEffect(() => {
    fetchRequiredServices();
    window.addEventListener(BOOKING_SERVICES_CHANGED, fetchRequiredServices);
    return () => {
      window.removeEventListener(
        BOOKING_SERVICES_CHANGED,
        fetchRequiredServices,
      );
    };
  }, [fetchRequiredServices]);

  if (!company || !company.stageId) return null;

  const lead = {
    fullName: company.clientName || '',
    email: company.contactEmail || '',
    phone: company.contactNumber || '',
    address: company.address || '',
    postcode: company.postcode || company.postCode || '',
    message: company.message || '',
  };

  const stages = Object.values(pipeline).sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const currentIndex = stages.findIndex((s) => s.id === company.stageId);
  const currentStageName =
    company.stageIdName ||
    stages[currentIndex]?.title ||
    'Current stage';

  return (
    <div className="profile-container">
      {typeof onBack === 'function' && (
        <button
          type="button"
          className="profile-back-btn"
          onClick={onBack}
          aria-label="Back to pipeline"
        >
          <FaArrowLeft aria-hidden />
          <span>Back to pipeline</span>
        </button>
      )}
      <div className="profile-header">
        <div className="pipeline-header">
          <div>
            <h2 className="company-title">{company.clientName}</h2>

            <p className="text-sm">
              Estimated opportunity value:{' '}
              {formatAUD(company.estimatedPRice ?? 0)}
            </p>

            <p className="text-sm">Owner: {token || 'Unknown'}</p>
          </div>

          <div className="header-actions">
            <button
              className="btn-mail"
              onClick={() => setShowEmailModal(true)}
            >
              Email client
            </button>
          </div>
        </div>

        <div className="stage-progress">
          {stages.map((stage, idx) => (
            <React.Fragment key={stage.id}>
              <div className="stage-progress-item">
                {idx === currentIndex && (
                  <div className="active-stage-label">{currentStageName}</div>
                )}
                <div
                  className={`stage-progress-circle ${
                    idx < currentIndex
                      ? 'done'
                      : idx === currentIndex
                        ? 'active'
                        : ''
                  }`}
                >
                  {idx < currentIndex && <span className="tick">✓</span>}
                </div>
              </div>

              {idx !== stages.length - 1 && (
                <div
                  className={`stage-progress-line ${
                    idx < currentIndex ? 'filled' : ''
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="profile-body">
        <div className="left-card">
          <div className="contact-block">
            <div className="user-icon">
              {(company.clientName || '?').charAt(0)}
            </div>
            <div>
              <h3>{company.clientName}</h3>
              <p>{company.contactNumber}</p>
              <p>{company.contactEmail}</p>
              <p>{company.address}</p>
            </div>
          </div>

          <h4>Opportunity description</h4>
          <p className="desc-field">
            {company.message || 'No description added'}
          </p>

          <h4>Required service</h4>
          <div className="required-services">
            {requiredServices.length > 0 ? (
              requiredServices.map((row) => (
                <span key={row.id || row.name} className="btn-product">
                  {row.name}
                </span>
              ))
            ) : (
              <span className="btn-product btn-product--empty">+ Service</span>
            )}
          </div>
        </div>

        <div className="right-content">
          <MainPage bookingId={company?.id} lead={lead} />
        </div>
      </div>
      {showEmailModal && (
        <WriteEmailModal
          onClose={() => setShowEmailModal(false)}
          recipientEmail={company?.contactEmail}
        />
      )}
    </div>
  );
}
